import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';
import SmartiLabIcon from './SmartiLabIcon';
import styles from './SmartiLabEntranceOverlay.module.css';

const DEFAULT_DURATION_MS = 10000;
const REDUCED_DURATION_MS = 1500;

const particles = [
  { x: 20, y: 34, delay: '0s' },
  { x: 82, y: 22, delay: '0.6s' },
  { x: 144, y: 36, delay: '1.1s' },
  { x: 212, y: 62, delay: '0.4s' },
  { x: 236, y: 128, delay: '0.9s' },
  { x: 202, y: 198, delay: '0.2s' },
  { x: 138, y: 236, delay: '0.7s' },
  { x: 74, y: 228, delay: '1.3s' },
  { x: 26, y: 176, delay: '0.5s' },
  { x: 54, y: 98, delay: '1.0s' },
];

const dataStreams = [
  { width: '42%', top: '14%', delay: '0s' },
  { width: '58%', top: '20%', delay: '0.8s' },
  { width: '36%', top: '74%', delay: '0.3s' },
  { width: '52%', top: '80%', delay: '1.1s' },
];

const phaseSteps = [
  {
    key: 'awakening',
    until: 0.12,
    it: {
      eyebrow: 'Ingresso Smarti Lab',
      title: 'Avvio Smarti Lab',
      subtitle: 'L’area di indagine viene isolata dal dashboard principale e prepara il motore ML.',
      status: 'Preparazione motore ML',
    },
    en: {
      eyebrow: 'Entering Smarti Lab',
      title: 'Starting Smarti Lab',
      subtitle: 'The investigation area is separating from the main dashboard and preparing the ML engine.',
      status: 'Preparing ML engine',
    },
  },
  {
    key: 'expansion',
    until: 0.28,
    it: {
      eyebrow: 'Ingresso Smarti Lab',
      title: 'Preparazione motore ML',
      subtitle: 'Il sistema apre il contesto Smarti Lab e allinea i segnali di produzione e burn-in.',
      status: 'Inizializzazione kernel investigativo',
    },
    en: {
      eyebrow: 'Entering Smarti Lab',
      title: 'Preparing ML engine',
      subtitle: 'The system is opening the Smarti Lab context and aligning production and burn-in signals.',
      status: 'Initializing investigation kernel',
    },
  },
  {
    key: 'neural',
    until: 0.55,
    it: {
      eyebrow: 'Ingresso Smarti Lab',
      title: 'Analisi dati in corso',
      subtitle: 'Le strutture di indagine stanno caricando i profili di rischio, anomalia e collegamento.',
      status: 'Inizializzazione rete di evidenza',
    },
    en: {
      eyebrow: 'Entering Smarti Lab',
      title: 'Analyzing data',
      subtitle: 'The investigation structures are loading risk, anomaly, and linking profiles.',
      status: 'Initializing evidence network',
    },
  },
  {
    key: 'evidence',
    until: 0.85,
    it: {
      eyebrow: 'Ingresso Smarti Lab',
      title: 'Analisi dati in corso',
      subtitle: 'Il motore evidenze confronta anomalie, sequenze, collegamenti burn-in e contesti simili.',
      status: 'Calcolo evidenze e priorità',
    },
    en: {
      eyebrow: 'Entering Smarti Lab',
      title: 'Analyzing data',
      subtitle: 'The evidence engine is comparing anomalies, sequences, burn-in links, and similar contexts.',
      status: 'Scoring evidence and priority',
    },
  },
  {
    key: 'ready',
    until: 1,
    it: {
      eyebrow: 'Ingresso Smarti Lab',
      title: 'Motore ML pronto',
      subtitle: 'L’area di indagine è pronta. I risultati indicano priorità, non cause confermate.',
      status: 'Preparazione area di indagine',
    },
    en: {
      eyebrow: 'Entering Smarti Lab',
      title: 'ML engine ready',
      subtitle: 'The investigation area is ready. Results indicate priorities, not confirmed causes.',
      status: 'Preparing investigation area',
    },
  },
];

const rotatingLabels = [
  {
    it: 'Normalizzazione dati',
    en: 'Data normalization',
  },
  {
    it: 'Collegamento produzione e burn-in',
    en: 'Production and burn-in linking',
  },
  {
    it: 'Ricerca anomalie',
    en: 'Anomaly detection',
  },
  {
    it: 'Confronto con unità sane',
    en: 'Comparison with healthy units',
  },
  {
    it: 'Calcolo punteggi di evidenza',
    en: 'Evidence scoring',
  },
  {
    it: 'Preparazione area di indagine',
    en: 'Preparing investigation area',
  },
];

function getPhase(progress) {
  return phaseSteps.find((phase) => progress <= phase.until) || phaseSteps[phaseSteps.length - 1];
}

export default function SmartiLabEntranceOverlay({
  open,
  onComplete,
  durationMs = DEFAULT_DURATION_MS,
}) {
  const { i18n } = useTranslation();
  const prefersReducedMotion = usePrefersReducedMotion();
  const [progress, setProgress] = useState(0);
  const completedRef = useRef(false);
  const frameRef = useRef(null);
  const duration = prefersReducedMotion ? REDUCED_DURATION_MS : durationMs;
  const isItalian = i18n.language !== 'en';

  useEffect(() => {
    if (!open) {
      setProgress(0);
      completedRef.current = false;
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      return undefined;
    }

    const start = performance.now();
    const tick = (now) => {
      const nextProgress = Math.min(1, (now - start) / duration);
      setProgress(nextProgress);

      if (nextProgress >= 1) {
        if (!completedRef.current) {
          completedRef.current = true;
          onComplete?.();
        }
        return;
      }

      frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [open, duration, onComplete]);

  const phase = useMemo(() => getPhase(progress), [progress]);
  const copy = isItalian ? phase.it : phase.en;
  
  const fallbackLabel = {
    it: 'Preparazione motore ML',
    en: 'Preparing ML engine',
  };

  const safeLabels = rotatingLabels.length > 0 ? rotatingLabels : [fallbackLabel];

  const labelIndex = Number.isFinite(progress)
    ? Math.min(safeLabels.length - 1, Math.max(0, Math.floor(progress * safeLabels.length)))
    : 0;

  const activeLabel = safeLabels[labelIndex] || fallbackLabel;
  const statusLabel = isItalian
    ? activeLabel?.it ?? fallbackLabel.it
    : activeLabel?.en ?? fallbackLabel.en;
    
  const footer = isItalian
    ? 'Il sistema prepara l’area di indagine. I risultati indicano priorità, non cause confermate.'
    : 'The system prepares the investigation area. Results indicate priorities, not confirmed causes.';
  const visibleTicks = Math.max(1, Math.min(6, Math.ceil(progress * 6)));

  if (!open) return null;

  return (
    <div
      className={`${styles.overlay} ${styles[`phase${phase.key.charAt(0).toUpperCase()}${phase.key.slice(1)}`]} ${prefersReducedMotion ? styles.reducedMotion : ''}`}
      role="status"
      aria-live="polite"
      aria-label={isItalian ? 'Apertura Smarti Lab, preparazione motore ML.' : 'Opening Smarti Lab, preparing ML engine.'}
    >
      <div className={styles.backdropGrid} />
      <div className={styles.backdropGlow} />
      <div className={styles.scanLines} />

      <section className={styles.panel}>
        <div className={styles.halo} />
        <div className={styles.stageGlow} />
        <div className={styles.dataStreams}>
          {dataStreams.map((stream, index) => (
            <span
              key={`${stream.top}-${index}`}
              className={styles.dataStream}
              style={{ '--stream-width': stream.width, '--stream-top': stream.top, '--stream-delay': stream.delay }}
            />
          ))}
        </div>
        <div className={styles.orbitalShell}>
          <div className={styles.orbitAura} />
          <div className={styles.orbitRing} />
          <div className={styles.orbitRingAlt} />
          <div className={styles.orbitRingGhost} />
          {particles.map((particle, index) => (
            <span
              key={`${particle.x}-${particle.y}-${index}`}
              className={styles.particle}
              style={{ left: `${particle.x}px`, top: `${particle.y}px`, '--delay': particle.delay }}
            />
          ))}
          <div className={styles.iconWrap}>
            <div className={styles.iconGlow} />
            <SmartiLabIcon className={styles.iconSvg} />
          </div>
        </div>

        <div className={styles.copy}>
          <p className={styles.eyebrow}>{copy.eyebrow}</p>
          <h1 className={styles.title}>{copy.title}</h1>
          <p className={styles.subtitle}>{copy.subtitle}</p>
          <div className={styles.phaseTag}>{copy.status}</div>

          <div className={styles.statusLine}>
            <span className={styles.statusLabel}>{statusLabel}</span>
            <div className={styles.progressTrack}>
              <div className={styles.progressFill} style={{ '--progress': `${Math.round(progress * 100)}%` }} />
            </div>
            <div className={styles.progressTicks}>
              {Array.from({ length: 6 }).map((_, index) => (
                <span key={index} className={`${styles.tick} ${index < visibleTicks ? styles.tickActive : ''}`} />
              ))}
            </div>
          </div>

          <p className={styles.footer}>{footer}</p>
        </div>
      </section>
    </div>
  );
}
